import { useState } from "react";
import Badge from "./ui/Badge";
import Button from "./ui/Button";

// CRISP scoring engine ported 1:1 from the real app's five-dimension, two-pass model
// (groundworktierup-prod: src/components/TierUpTool.tsx). The Charter fields below track
// the real QLG54 template's core structure and tier-scaling, extended here with a few
// fields (duration, budget, stakeholders, risk summary) that make this demo a complete,
// standalone example rather than a fragment. Plain client-side state, no backend calls,
// nothing saved beyond the current page load.

const DIMENSION_ORDER = ["consequence", "risk", "impact", "scope", "pace"];

const DIMENSION_LABELS = {
  consequence: "Consequence",
  risk: "Risk",
  impact: "Impact",
  scope: "Scope",
  pace: "Pace",
};

const DELIVERY_OPTIONS = {
  consequence: [
    { value: 1, label: "1 - Low / Localised (low disruption risk, straightforward rollback)" },
    { value: 2, label: "2 - Medium / Departmental (moderate disruption, short operational delay)" },
    { value: 3, label: "3 - High / Enterprise-wide (critical business disruption, data or regulatory risk)" },
  ],
  risk: [
    { value: 1, label: "1 - Low / Standard (established solution, experienced team, low novelty)" },
    { value: 2, label: "2 - Medium / Complex (some integrations, moderately complex delivery path)" },
    { value: 3, label: "3 - High / Novel or unstable (first-of-its-kind, third-party reliance, unproven tech)" },
  ],
  impact: [
    { value: 1, label: "1 - Low / Frictionless (intuitive adjustment, self-directed learning)" },
    { value: 2, label: "2 - Medium / Targeted change (minor training required, standard workflow update)" },
    { value: 3, label: "3 - High / Transformative (major habit rewrite, intensive training and hypercare needed)" },
  ],
  scope: [
    { value: 1, label: "1 - Localised (single team or small isolated working group)" },
    { value: 2, label: "2 - Division / Departmental (cross-functional footprint affecting 2-5 teams)" },
    { value: 3, label: "3 - Enterprise-wide / Public (reaches entire workforce, customers, or all divisions)" },
  ],
  pace: [
    { value: 1, label: "1 - Gradual / Measured (phased over several quarters, flexible timelines)" },
    { value: 2, label: "2 - Steady / Standard (sprint-based or standard calendar delivery speed)" },
    { value: 3, label: "3 - Compressed / Urgent (hard external regulatory or executive mandate deadline)" },
  ],
};

// Same five dimensions, assessed a second time through a people lens, per QLG02.
const CHANGE_OPTIONS = {
  consequence: [
    { value: 1, label: "1 - Low / Contained (a small, already-engaged group affected)" },
    { value: 2, label: "2 - Medium / Departmental (a defined team or department feels this)" },
    { value: 3, label: "3 - High / Broad workforce (wide reach, or reputational/external exposure)" },
  ],
  risk: [
    { value: 1, label: "1 - Low / Bought in (neutral-to-positive reception expected)" },
    { value: 2, label: "2 - Medium / Mixed (some pushback likely, manageable with engagement)" },
    { value: 3, label: "3 - High / Resistance likely (significant behaviour or habit change required)" },
  ],
  impact: [
    { value: 1, label: "1 - Low / Minimal disruption to daily routine" },
    { value: 2, label: "2 - Medium / Noticeable workflow shift, some retraining" },
    { value: 3, label: "3 - High / Major habit rewrite, intensive coaching needed" },
  ],
  scope: [
    { value: 1, label: "1 - Localised (one team feels this)" },
    { value: 2, label: "2 - Departmental (2-5 teams affected)" },
    { value: 3, label: "3 - Enterprise-wide (all staff, or an external/public audience)" },
  ],
  pace: [
    { value: 1, label: "1 - Gradual (people have time to adjust)" },
    { value: 2, label: "2 - Steady (standard adoption timeline)" },
    { value: 3, label: "3 - Compressed (forced adoption on a hard deadline)" },
  ],
};

// QLG54 Charter fields, tier-scaled: Tier 3 gets a light four-field version, Tier 2 adds
// planning and delivery detail, Tier 1 adds a dedicated risk summary on top of that.
const CHARTER_FIELDS = [
  { key: "purposeProblem", label: "Purpose & problem", prompt: "Why is this happening, and what problem will it solve?", minTier: 3, multiline: true },
  { key: "inScopeNotes", label: "In scope", prompt: "What's explicitly in scope for this initiative?", minTier: 3, multiline: true },
  { key: "outOfScopeNotes", label: "Out of scope", prompt: "What's explicitly out of scope, or being deliberately deferred?", minTier: 3, multiline: true },
  { key: "objectivesCriteria", label: "Objectives & success criteria", prompt: "If this succeeds, what would actually be different? What does “done” look like?", minTier: 3, multiline: true },
  { key: "estimatedDuration", label: "Estimated duration", prompt: "How long, start to finish? e.g. “16 weeks” or “Q1–Q3 2027”.", minTier: 2, multiline: false },
  { key: "estimatedBudget", label: "Estimated budget", prompt: "Ballpark investment, including any vendor or specialist cost. e.g. “$180,000”.", minTier: 2, multiline: false },
  { key: "keyStakeholders", label: "Key stakeholders", prompt: "Who needs to be bought in, informed, or consulted before this moves?", minTier: 2, multiline: true },
  { key: "technicalNotes", label: "Technical considerations", prompt: "Platform/tools, data sources, integrations, data classification, anything already known.", minTier: 2, multiline: true },
  { key: "assumptionsConstraints", label: "Assumptions & constraints", prompt: "What are we assuming to be true? What's constraining delivery, budget, timeline, resourcing, technology?", minTier: 2, multiline: true },
  { key: "keyRisks", label: "Key risks", prompt: "What's most likely to derail this, and what would you do about it?", minTier: 1, multiline: true },
];

function getCharterFieldsForTier(tier) {
  const effectiveTier = tier || 3;
  return CHARTER_FIELDS.filter((f) => effectiveTier <= f.minTier);
}

function calcTier(s) {
  const score = s.consequence + s.risk + s.impact + s.scope + s.pace;
  let tier = 3;
  let tierName = "Tier 3, Operational / Low Complexity";
  if (score >= 12 || (s.consequence === 3 && s.scope === 3)) {
    tier = 1;
    tierName = "Tier 1, Enterprise / High Complexity";
  } else if (score >= 8) {
    tier = 2;
    tierName = "Tier 2, Departmental / Medium Complexity";
  }
  return { tier, tierName, score };
}

const DEFAULT_SCORES = { consequence: 2, risk: 2, impact: 2, scope: 2, pace: 2 };

const TIER_COLOR = {
  1: { fg: "#EF4444", bg: "#FEE2E2" },
  2: { fg: "var(--amber)", bg: "var(--amber-light)" },
  3: { fg: "var(--green)", bg: "var(--green-light)" },
};

function parseOption(label) {
  const match = label.match(/^(\d+) - ([^(]+?)\s*(?:\((.+)\))?$/);
  if (!match) return { num: "", title: label, detail: "" };
  return { num: match[1], title: match[2].trim(), detail: match[3] || "" };
}

function TierBadge({ label, result }) {
  const c = TIER_COLOR[result.tier];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        fontWeight: 700,
        padding: "5px 12px",
        borderRadius: 20,
        color: c.fg,
        background: c.bg,
        whiteSpace: "nowrap",
      }}
    >
      {label ? `${label}: ` : ""}T{result.tier} &middot; {result.tierName.split(", ")[1]}
    </span>
  );
}

function DimensionRow({ dim, options, value, onChange }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <p style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", margin: "0 0 8px" }}>
        {DIMENSION_LABELS[dim]}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8 }}>
        {options.map((opt) => {
          const { num, title, detail } = parseOption(opt.label);
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              aria-pressed={active}
              style={{
                textAlign: "left",
                padding: "10px 12px",
                borderRadius: "var(--radius)",
                border: active ? "1px solid var(--lilac-deep)" : "1px solid var(--border-strong)",
                background: active ? "var(--lilac-wash)" : "var(--surface-0)",
                transition: "all var(--transition-fast)",
              }}
            >
              <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--navy)" }}>
                {num}. {title}
              </span>
              {detail && (
                <span style={{ display: "block", fontSize: 11, color: "var(--text-secondary)", marginTop: 2, lineHeight: 1.4 }}>
                  {detail}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CharterField({ num, field, value, onChange }) {
  const Tag = field.multiline ? "textarea" : "input";
  const id = `charter-field-${field.key}`;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
        <span aria-hidden="true" style={{ fontFamily: "var(--font-voice)", fontStyle: "italic", fontSize: 15, color: "var(--lilac-deep)" }}>
          {num}
        </span>
        <label htmlFor={id} style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)" }}>
          {field.label}
        </label>
      </div>
      <div style={{ paddingLeft: 25 }}>
        <Tag
          id={id}
          className="charter-field"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.prompt}
          rows={field.multiline ? 3 : undefined}
          style={{ fontSize: 13.5, lineHeight: 1.6, display: "block" }}
        />
      </div>
    </div>
  );
}

const DEFAULT_ANSWERS = {};

export default function CrispCharterDemo() {
  const [phase, setPhase] = useState("intake");
  const [name, setName] = useState("");
  const [preparedBy, setPreparedBy] = useState("");
  const [desc, setDesc] = useState("");
  const [deliveryScores, setDeliveryScores] = useState(DEFAULT_SCORES);
  const [changeScores, setChangeScores] = useState(DEFAULT_SCORES);
  const [charterAnswers, setCharterAnswers] = useState(DEFAULT_ANSWERS);
  const [charterRef, setCharterRef] = useState("");
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [exportError, setExportError] = useState(false);

  const deliveryResult = calcTier(deliveryScores);
  const changeResult = calcTier(changeScores);
  const charterFields = getCharterFieldsForTier(deliveryResult.tier);
  const activeScores = phase === "change" ? changeScores : deliveryScores;
  const setActiveScores = phase === "change" ? setChangeScores : setDeliveryScores;
  const activeOptions = phase === "change" ? CHANGE_OPTIONS : DELIVERY_OPTIONS;

  const today = new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

  function updateCharterAnswer(key, value) {
    setCharterAnswers((prev) => ({ ...prev, [key]: value }));
  }

  function reset() {
    setPhase("intake");
    setName("");
    setDesc("");
    setDeliveryScores(DEFAULT_SCORES);
    setChangeScores(DEFAULT_SCORES);
    setCharterAnswers(DEFAULT_ANSWERS);
    setCharterRef("");
    setStartedAt(null);
    setElapsed(null);
  }

  async function handleDownloadPdf() {
    setDownloading(true);
    setExportError(false);
    try {
      // This demo has no account behind it, so the export endpoint requires a short-lived,
      // single-use nonce issued just before the real request - fetched fresh every click
      // rather than reused, since each one is consumed on first use.
      const nonceRes = await fetch("https://groundworktierup.app/api/charter/export-nonce");
      if (!nonceRes.ok) throw new Error("PDF export failed");
      const { nonce } = await nonceRes.json();

      const response = await fetch("https://groundworktierup.app/api/charter/export-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          charterRef,
          initiativeName: name,
          preparedBy,
          date: today,
          deliveryTier: `T${deliveryResult.tier} · ${deliveryResult.tierName.split(", ")[1]}`,
          changeTier: `T${changeResult.tier} · ${changeResult.tierName.split(", ")[1]}`,
          fieldsShown: charterFields.length,
          fieldsTotal: CHARTER_FIELDS.length,
          fields: charterFields.map((f) => ({ label: f.label, value: charterAnswers[f.key] || "" })),
          exportNonce: nonce
        })
      });
      if (!response.ok) throw new Error("PDF export failed");

      const blob = await response.blob();
      const filename =
        response.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1] ?? "Charter.pdf";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportError(true);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <section
      className="glass-card"
      style={{
        maxWidth: 880,
        margin: "0 auto 4rem",
        padding: "2.5rem",
        background: "linear-gradient(135deg, var(--surface-0) 0%, var(--surface-1) 100%)",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ marginBottom: 14 }}>
          <Badge>Try It Yourself</Badge>
        </div>
        <h2 style={{ fontFamily: "var(--font-voice)", fontSize: 26, color: "var(--navy)", margin: "0 0 10px" }}>
          Idea to Charter, for real
        </h2>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", maxWidth: 560, margin: "0 auto", lineHeight: 1.6 }}>
          The same CRISP scoring engine that runs inside Groundwork TierUp, with a Charter based on the in-app template.
          Nothing you type here is saved, and it clears when you leave. If you download the PDF, your answers go to our server once to build the file, then they&rsquo;re discarded. They aren&rsquo;t stored or sent to any AI.
        </p>
      </div>

      {phase === "intake" && (
        <div style={{ maxWidth: 480, margin: "0 auto" }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", display: "block", marginBottom: 6 }}>
            Initiative name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. CRM System Upgrade"
            style={{
              width: "100%",
              padding: "10px 14px",
              borderRadius: "var(--radius)",
              border: "1px solid var(--border-strong)",
              fontSize: 14,
              marginBottom: 16,
              fontFamily: "inherit",
            }}
          />
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", display: "block", marginBottom: 6 }}>
            Your name
          </label>
          <input
            value={preparedBy}
            onChange={(e) => setPreparedBy(e.target.value)}
            placeholder="e.g. Alex Rivera"
            style={{
              width: "100%",
              padding: "10px 14px",
              borderRadius: "var(--radius)",
              border: "1px solid var(--border-strong)",
              fontSize: 14,
              marginBottom: 16,
              fontFamily: "inherit",
            }}
          />
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", display: "block", marginBottom: 6 }}>
            Problem / business case statement
          </label>
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="What strategic issue does this resolve? Define the goal and target outcome..."
            rows={3}
            style={{
              width: "100%",
              padding: "10px 14px",
              borderRadius: "var(--radius)",
              border: "1px solid var(--border-strong)",
              fontSize: 14,
              marginBottom: 20,
              fontFamily: "inherit",
              resize: "vertical",
            }}
          />
          <Button
            block
            disabled={!name.trim() || !preparedBy.trim()}
            onClick={() => {
              setStartedAt(Date.now());
              setPhase("delivery");
            }}
          >
            Start CRISP Assessment &rarr;
          </Button>
        </div>
      )}

      {(phase === "delivery" || phase === "change") && (
        <div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: "var(--lilac-deep)", textTransform: "uppercase", letterSpacing: "0.04em", margin: 0 }}>
              {phase === "delivery" ? "Step 1 of 2: CRISP Delivery Pass, can we deliver this?" : "Step 2 of 2: CRISP Change Pass, how will people take it?"}
            </p>
            <div aria-live="polite">
              <TierBadge result={phase === "delivery" ? deliveryResult : changeResult} />
            </div>
          </div>
          <p style={{ fontSize: 12, color: "var(--text-secondary)", margin: "0 0 20px" }}>
            {phase === "delivery"
              ? "Five dimensions, scored 1 to 3. All default to 2, adjust any that don't fit."
              : "Same five dimensions, scored again through a people lens."}
          </p>

          {DIMENSION_ORDER.map((dim) => (
            <DimensionRow
              key={dim}
              dim={dim}
              options={activeOptions[dim]}
              value={activeScores[dim]}
              onChange={(val) => setActiveScores({ ...activeScores, [dim]: val })}
            />
          ))}

          <div style={{ display: "flex", justifyContent: phase === "delivery" ? "flex-end" : "space-between", marginTop: 8 }}>
            {phase === "change" && (
              <Button variant="secondary" onClick={() => setPhase("delivery")}>
                &larr; Back
              </Button>
            )}
            <Button
              onClick={() => {
                if (phase === "delivery") {
                  setPhase("change");
                } else {
                  setCharterAnswers({ purposeProblem: desc });
                  setCharterRef(`GW-CH-${Math.floor(1000 + Math.random() * 9000)}`);
                  setElapsed(((Date.now() - startedAt) / 1000).toFixed(1));
                  setPhase("reveal");
                }
              }}
            >
              {phase === "delivery" ? "Next: CRISP Change Pass →" : "Generate Charter →"}
            </Button>
          </div>
        </div>
      )}

      {phase === "reveal" && (
        <div>
          <div
            style={{
              background: "var(--surface-0)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
              overflow: "hidden",
              boxShadow: "var(--shadow-lg)",
              marginBottom: 24,
            }}
          >
            <div style={{ height: 5, background: "linear-gradient(90deg, var(--navy), var(--lilac-deep))" }} aria-hidden="true" />
            <div style={{ padding: "2rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 22 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--navy)" }}>
                  Groundwork<span style={{ color: "var(--lilac-deep)" }}>TierUp</span>
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "var(--lilac-deep)",
                    background: "var(--lilac-wash)",
                    padding: "4px 12px",
                    borderRadius: 20,
                  }}
                >
                  Project Charter
                </span>
              </div>

              <p style={{ fontFamily: "var(--font-voice)", fontSize: 24, color: "var(--navy)", margin: "0 0 18px" }}>
                {name || "Untitled Initiative"}
              </p>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  borderTop: "1px solid var(--border)",
                  borderBottom: "1px solid var(--border)",
                  marginBottom: 18,
                }}
              >
                {[
                  { label: "Charter Ref", value: charterRef },
                  { label: "Prepared By", value: preparedBy },
                  { label: "Date", value: today },
                ].map((item, idx, arr) => (
                  <div
                    key={item.label}
                    style={{
                      padding: "10px 18px 10px 0",
                      marginRight: 18,
                      borderRight: idx < arr.length - 1 ? "1px solid var(--border)" : "none",
                    }}
                  >
                    <span style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 2 }}>
                      {item.label}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{item.value}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                <TierBadge label="Delivery" result={deliveryResult} />
                <TierBadge label="Change" result={changeResult} />
              </div>
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 0 26px" }}>
                Charter scaffolded in <strong style={{ color: "var(--text-secondary)" }}>{elapsed}s</strong>, scaled to {charterFields.length} of {CHARTER_FIELDS.length} fields for a Tier {deliveryResult.tier} initiative.
              </p>

              {charterFields.map((f, idx) => (
                <CharterField
                  key={f.key}
                  num={idx + 1}
                  field={f}
                  value={charterAnswers[f.key] || ""}
                  onChange={(val) => updateCharterAnswer(f.key, val)}
                />
              ))}
            </div>
            <div style={{ background: "var(--lilac-wash)", padding: "14px 2rem", textAlign: "center" }}>
              <p style={{ fontSize: 11.5, color: "var(--text-secondary)", margin: 0 }}>
                Generated by Groundwork TierUp &middot; not saved; used only to build this PDF
              </p>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
              <Button onClick={handleDownloadPdf} disabled={downloading}>
                {downloading ? "Generating PDF…" : <>Download PDF &darr;</>}
              </Button>
              <Button variant="secondary" onClick={reset}>
                Try another initiative
              </Button>
            </div>
            {exportError && (
              <p style={{ fontSize: 12.5, color: "#DC2626", fontWeight: 600, margin: 0 }}>
                Export failed. Please try again.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
